import React from 'react';
import { DropzoneProps, useDropzone } from 'react-dropzone';
// @mui
import {
  Box,
  IconButton,
  Typography,
  SxProps,
  StackProps,
  styled,
  alpha,
} from '@mui/material';

import MultiFilePreview from './preview/MultiFilePreview';
import SingleFilePreview from './preview/SingleFilePreview';
import { Close, FileUploadOutlined } from '@mui/icons-material';

// ----------------------------------------------------------------------
interface iAppUpload extends DropzoneProps {
  sx?: SxProps;
  error?: boolean;
  success?: boolean;
  files?: any[];
  file?: any;
  disabled?: boolean;
  onDelete?: () => void;
  onRemove?: (file: any) => void;
  onUpload?: () => void;
  thumbnail?: boolean;
  helperText?: React.ReactNode;
  onRemoveAll?: () => void;
}

const StyledDropZone = styled('div')(({ theme }) => ({
  outline: 'none',
  cursor: 'pointer',
  overflow: 'hidden',
  position: 'relative',
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create('padding'),
  backgroundColor: '#f4f6f8',
  border: `2px dashed ${alpha(theme.palette.grey[500], 0.32)}`,
  transitionDuration: '0.3s',
  opacity: 0.72,
  '&:hover': {
    opacity: 1,
  },
}));

const AppUpload: React.FC<iAppUpload> = ({
  disabled,
  multiple = false,
  error,
  helperText,
  file,
  success,
  onDelete,
  files,
  thumbnail = false,
  // onUpload,
  onRemove,
  // onRemoveAll,
  sx,
  ...other
}) => {
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    // fileRejections,
  } = useDropzone({
    multiple,
    disabled,
    ...other,
  });

  const hasFile = !!file && !multiple;
  const hasFiles = files && multiple && files.length > 0;
  const isError = isDragReject || !!error;

  return (
    <Box
      sx={{
        width: hasFile || hasFiles ? 1 : '100%',
        position: 'relative',
        ...sx,
      }}
    >
      <StyledDropZone
        {...getRootProps()}
        sx={{
          ...(isDragActive && {
            opacity: 1,
          }),
          ...(isError && {
            color: 'error.main',
            bgcolor: 'error.lighter',
            borderColor: 'error.light',
          }),
          ...(success && {
            color: 'success.main',
            bgcolor: 'success.lighter',
            borderColor: 'success.light',
          }),
          ...(disabled && {
            opacity: 0.48,
            pointerEvents: 'none',
          }),
          // ...(hasFile && {
          //   padding: '12% 0',
          // }),
          ...((hasFile || hasFiles) && {
            padding: '3%',
            opacity: '1!important',
          }),
        }}
      >
        <input
          type="file"
          accept="image/*, application/pdf"
          {...getInputProps()}
        />

        {!hasFile && !hasFiles && <Placeholder />}

        {hasFile && <SingleFilePreview file={file} />}

        {hasFiles && (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
                gridGap: 8,
              }}
            >
              <MultiFilePreview
                files={files}
                thumbnail={thumbnail}
                onRemove={onRemove}
              />
            </Box>
          </>
        )}
      </StyledDropZone>

      {helperText && helperText}

      {hasFile && onDelete && (
        <IconButton
          size="small"
          onClick={onDelete}
          sx={{
            transform: 'scale(0.8)',
            top: 4,
            right: 4,
            zIndex: 9,
            position: 'absolute',
            color: (theme) => alpha(theme.palette.common.white, 0.8),
            bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.48),
            },
          }}
        >
          <Close />
        </IconButton>
      )}
    </Box>
  );
};

export default AppUpload;

// ----------------------------------------------------------------------

const Placeholder: React.FC<StackProps> = ({ ...other }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: 100,
      }}
      {...other}
    >
      <FileUploadOutlined fontSize="large" color="disabled" />

      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', mt: 1, fontSize: 12 }}
      >
        Drag & Drop, or <strong>Browse</strong>
      </Typography>
    </Box>
  );
};
